import type { AIToolsConfig } from '@affine/core/modules/ai-button';
import { MessageContextItemKind } from '@affine/graphql';
import {
  parseWorkspaceViewSnapshot,
  type WorkspaceViewRevision,
  type WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';

import type { ReasoningModeId } from '../../../modules/ai-button/services/models';
import {
  type CanonicalMessageContext,
  type CanonicalMessageContextItem,
} from '../components/ai-chat-chips/canonical-message-context';
import { AIProvider } from './ai-provider';
import { getClientInstanceId } from './client-instance';
import { type CopilotClient, Endpoint } from './copilot-client';
import { type AffineTextEvent, delay, toTextStream } from './event-source';

const TIMEOUT = 50000;

export type TextToTextOptions = {
  client: CopilotClient;
  sessionId: string;
  threadId: string; // Required for thread isolation
  content?: string;
  attachments?: (string | Blob | File)[];
  context?: CanonicalMessageContext;
  timeout?: number;
  stream?: boolean;
  /**
   * The chat answers: the message waits in the chat's queue on the server
   * and the stream yields nothing.
   */
  queue?: boolean;
  signal?: AbortSignal;
  assertContextCurrent?: () => void;
  retry?: boolean;
  // user-message ID being retried. REQUIRED when retry === true so the
  // backend's trackStreamUsage can correlate tool-loop sub-calls under the
  // original interaction (triggerId). On non-retry calls a new user-message
  // is created via createMessage() and its id is used instead.
  messageId?: string;
  endpoint?: Endpoint;
  isRootSession?: boolean;
  postfix?: (text: string) => string;
  reasoning?: boolean;
  reasoningEffort?: ReasoningModeId;
  webSearch?: boolean;
  modelId?: string;
  toolsConfig?: AIToolsConfig;
  enableTools?: boolean;
  // E010-T015: Viewport position for Edgeless canvas
  viewportX?: number;
  viewportY?: number;
  // sl-535: User language for AI responses (ISO 639-1 code)
  language?: string;
  // sl-ddz8.5.3: Bot ID for Expert Agent chat
  mateId?: string;
  // sl-uq2j: Active document ID for message context (workspace-scoped chat)
  docId?: string;
  // Workspace scope of the chat — sent with stream requests so the backend
  // can validate client/workspace binding for canvas mutations.
  workspaceId?: string;
  workspaceViewScope?: WorkspaceViewScope;
  workspaceViewRevision?: WorkspaceViewRevision;
};

export type ToImageOptions = TextToTextOptions & {
  seed?: string;
};

/** Own the wait's signal and callbacks; a late native codec result is discarded. */
function imagePreparationStep<T>(
  signal: AbortSignal | undefined,
  start: (
    resolve: (value: T) => void,
    reject: (error: unknown) => void
  ) => () => void
): Promise<T> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    let settled = false;
    let dispose: (() => void) | undefined;
    const finish = (result: { value: T } | { error: unknown }) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', abort);
      dispose?.();
      if ('error' in result) reject(result.error);
      else resolve(result.value);
    };
    const abort = () => finish({ error: signal?.reason });
    signal?.addEventListener('abort', abort, { once: true });
    try {
      dispose = start(
        value => finish({ value }),
        error => finish({ error })
      );
      if (settled) dispose();
    } catch (error) {
      finish({ error });
    }
  });
}

async function resizeImage(
  blob: Blob | File,
  signal?: AbortSignal,
  assertContextCurrent?: () => void
): Promise<Blob> {
  const check = () => {
    signal?.throwIfAborted();
    assertContextCurrent?.();
  };
  check();
  const src = URL.createObjectURL(blob);
  const img = new Image();
  let canvas: HTMLCanvasElement | undefined;
  try {
    await imagePreparationStep<void>(signal, (resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    });
    check();
    if (!(img.width > 0 && img.height > 0))
      throw new Error('Invalid image dimensions');
    // Keep an already admitted JPEG, including its original compressed bytes.
    if (blob.type === 'image/jpeg' && img.width <= 1024 && img.height <= 1024) {
      const signature = new Uint8Array(await blob.slice(0, 3).arrayBuffer());
      check();
      if (
        signature[0] === 0xff &&
        signature[1] === 0xd8 &&
        signature[2] === 0xff
      )
        return blob;
    }
    canvas = document.createElement('canvas');
    const scale = Math.min(1, 1024 / img.width, 1024 / img.height);
    canvas.width = Math.max(1, Math.floor(img.width * scale));
    canvas.height = Math.max(1, Math.floor(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to prepare image canvas');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await imagePreparationStep<Blob>(signal, (resolve, reject) => {
      let current = true;
      canvas!.toBlob(
        value => {
          if (!current) return;
          if (value) resolve(value);
          else reject(new Error('Failed to encode image attachment'));
        },
        'image/jpeg',
        0.8
      );
      return () => {
        current = false;
      };
    });
  } finally {
    img.onload = null;
    img.onerror = null;
    img.src = '';
    URL.revokeObjectURL(src);
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}

interface CreateMessageOptions {
  client: CopilotClient;
  workspaceId: string;
  sessionId: string;
  threadId: string; // Required for thread isolation
  content?: string;
  attachments?: (string | Blob | File)[];
  context?: TextToTextOptions['context'];
  signal?: AbortSignal;
  assertContextCurrent?: () => void;
}

async function prepareUploadFile(
  attachment: Blob | File,
  sessionId: string,
  signal?: AbortSignal,
  assertContextCurrent?: () => void
): Promise<File> {
  signal?.throwIfAborted();
  assertContextCurrent?.();
  if (!attachment.type.startsWith('image/')) {
    return new File(
      [attachment],
      attachment instanceof File ? attachment.name : sessionId,
      {
        type: attachment.type,
      }
    );
  }

  const resized = await resizeImage(attachment, signal, assertContextCurrent);
  signal?.throwIfAborted();
  if (!resized) {
    throw new Error('Failed to prepare image attachment for upload');
  }

  return new File(
    [resized],
    attachment instanceof File ? attachment.name || sessionId : sessionId,
    {
      type: resized.type,
    }
  );
}

async function buildUploadPlan(
  attachments: (string | Blob | File)[] | undefined,
  sessionId: string,
  signal?: AbortSignal,
  assertContextCurrent?: () => void
) {
  signal?.throwIfAborted();
  assertContextCurrent?.();
  const stringAttachments: string[] = [];
  const blobAttachments: Array<Blob | File> = [];

  for (const attachment of attachments ?? []) {
    if (typeof attachment === 'string') {
      stringAttachments.push(attachment);
    } else {
      blobAttachments.push(attachment);
    }
  }

  const blobs: File[] = [];
  const prepared = new Map<Blob, File>();
  // Preserve every upload position while decoding/encoding each native alias once.
  for (const attachment of blobAttachments) {
    signal?.throwIfAborted();
    assertContextCurrent?.();
    let file = prepared.get(attachment);
    if (!file) {
      file = await prepareUploadFile(
        attachment,
        sessionId,
        signal,
        assertContextCurrent
      );
      signal?.throwIfAborted();
      assertContextCurrent?.();
      prepared.set(attachment, file);
    }
    blobs.push(file);
  }

  return { stringAttachments, blobs };
}

function hasUploadRef(item: CanonicalMessageContextItem) {
  return (
    typeof item.refs.uploadIndex === 'number' ||
    typeof item.refs.thumbnailUploadIndex === 'number'
  );
}

function stripClientOnlyContextPreviews(
  context: CanonicalMessageContext | undefined
): CanonicalMessageContext | undefined {
  if (!context) return undefined;

  return {
    ...context,
    items: context.items.map(item => {
      if (
        hasUploadRef(item) &&
        (item.kind === MessageContextItemKind.IMAGE ||
          item.kind === MessageContextItemKind.PDF_FILE)
      ) {
        return {
          ...item,
          display: {
            ...item.display,
            preview: null,
          },
        };
      }
      return item;
    }),
  };
}

async function createMessage({
  client,
  workspaceId,
  sessionId,
  threadId,
  content,
  attachments,
  context,
  signal,
  assertContextCurrent,
}: CreateMessageOptions): Promise<string> {
  if (signal?.aborted) throw signal.reason;

  const uploadPlan = await buildUploadPlan(
    attachments,
    sessionId,
    signal,
    assertContextCurrent
  );
  if (signal?.aborted) throw signal.reason;

  const options: Parameters<CopilotClient['createMessage']>[0] = {
    workspaceId,
    sessionId,
    threadId,
    content,
    context: stripClientOnlyContextPreviews(context),
  };

  if (uploadPlan.stringAttachments.length > 0) {
    options.attachments = uploadPlan.stringAttachments;
  }
  if (uploadPlan.blobs.length > 0) {
    options.blobs = uploadPlan.blobs;
  }

  assertContextCurrent?.();
  const id = await client.createMessage(options, signal, assertContextCurrent);
  if (signal?.aborted) throw signal.reason;
  return id;
}

export function textToText({
  client,
  sessionId,
  threadId,
  content,
  attachments,
  context,
  stream,
  queue,
  signal,
  timeout = TIMEOUT,
  retry = false,
  endpoint = Endpoint.Stream,
  postfix,
  reasoning,
  reasoningEffort,
  webSearch,
  modelId,
  toolsConfig,
  enableTools,
  // E010-T015: Viewport position for Edgeless canvas
  viewportX,
  viewportY,
  // sl-535: User language for AI responses
  language,
  // sl-ddz8.5.3: Bot ID for Expert Agent chat
  mateId,
  // sl-uq2j: Active document ID for message context
  docId,
  workspaceId,
  workspaceViewScope,
  workspaceViewRevision,
  messageId: retryMessageId,
  assertContextCurrent,
}: TextToTextOptions) {
  // sl-535: Get language from document.documentElement.lang if not provided
  // This is set by the i18n service when language changes
  if (signal?.aborted) throw signal.reason;

  // Reuse this turn's canonical envelope; never invoke a second surface reader.
  const viewItems =
    context?.items.filter(
      item => item.kind === MessageContextItemKind.WORKSPACE_VIEW
    ) ?? [];
  let viewBinding:
    | {
        workspaceViewScope: WorkspaceViewScope;
        workspaceViewRevision: WorkspaceViewRevision;
      }
    | undefined;
  if (viewItems.length || workspaceViewScope || workspaceViewRevision) {
    if (!workspaceId || viewItems.length !== 1)
      throw new Error('Missing workspace view request context');
    const item = viewItems[0];
    const parsed = parseWorkspaceViewSnapshot(item.payload, workspaceId);
    if (
      !parsed.ok ||
      item.refs.workspaceId !== workspaceId ||
      item.refs.viewId !== parsed.snapshot.viewId
    ) {
      throw new Error('Invalid workspace view request context');
    }
    const view = parsed.snapshot;
    if (
      (workspaceViewScope &&
        (workspaceViewScope.workspaceId !== view.workspaceId ||
          workspaceViewScope.viewId !== view.viewId)) ||
      (workspaceViewRevision &&
        (workspaceViewRevision.epoch !== view.revision.epoch ||
          workspaceViewRevision.sequence !== view.revision.sequence))
    ) {
      throw new Error('Workspace view stream binding does not match this turn');
    }
    viewBinding = {
      workspaceViewScope: {
        workspaceId: view.workspaceId,
        viewId: view.viewId,
      },
      workspaceViewRevision: view.revision,
    };
  }

  const resolvedLanguage =
    language ||
    (typeof document !== 'undefined'
      ? document.documentElement.lang
      : undefined);

  if (retry && !retryMessageId) {
    throw new Error(
      'textToText: retry=true requires messageId of the user message being retried'
    );
  }

  let messageId: string | undefined = retry ? retryMessageId : undefined;

  if (stream) {
    return {
      [Symbol.asyncIterator]: async function* () {
        if (signal?.aborted) throw signal.reason;
        if (!workspaceId) {
          throw new Error('textToText requires workspaceId');
        }
        if (!retry) {
          messageId = await createMessage({
            client,
            workspaceId,
            sessionId,
            threadId,
            content,
            attachments,
            context,
            signal,
            assertContextCurrent,
          });
        }
        const streamOptions = {
          sessionId,
          threadId,
          messageId,
          reasoning,
          reasoningEffort,
          webSearch,
          modelId,
          toolsConfig,
          enableTools,
          // E010-T015: Pass viewport to backend
          viewportX,
          viewportY,
          // sl-535: Pass language to backend
          language: resolvedLanguage,
          // sl-ddz8.5.3: Pass bot ID to backend
          mateId,
          // Closed-loop canvas mutations: bind this stream to this tab,
          // the active doc, and the workspace.
          clientInstanceId: getClientInstanceId(),
          activeDocId: docId,
          workspaceId,
          ...viewBinding,
        };
        if (queue) {
          await client.queueChatMessage(streamOptions);
          return;
        }
        const eventSource = client.chatTextStream(streamOptions, endpoint);
        AIProvider.LAST_ACTION_SESSIONID = sessionId;

        let abortStream: (() => void) | undefined;
        if (signal) {
          // Closing the EventSource leaves the answer running on the server;
          // only the stop request ends it.
          abortStream = () =>
            stopChatTurn(eventSource, client, sessionId, workspaceId);
          if (signal.aborted) {
            abortStream();
            return;
          }
          signal.addEventListener('abort', abortStream, { once: true });
        }
        try {
          if (postfix) {
            const messages: string[] = [];
            for await (const event of toTextStream(eventSource, {
              timeout,
              signal,
            })) {
              if (event.type === 'message') {
                messages.push(event.data);
              }
            }
            yield postfix(messages.join(''));
          } else {
            yield* toChatStreamChunks(
              toTextStream(eventSource, { timeout, signal })
            );
          }
        } finally {
          if (abortStream) signal?.removeEventListener('abort', abortStream);
        }
      },
    };
  } else {
    return Promise.race([
      timeout
        ? delay(timeout).then(() => {
            throw new Error('Timeout');
          })
        : null,
      (async function () {
        if (!workspaceId) {
          throw new Error('chatText requires workspaceId');
        }
        if (!retry) {
          messageId = await createMessage({
            client,
            workspaceId,
            sessionId,
            threadId,
            content,
            attachments,
            context,
            signal,
            assertContextCurrent,
          });
        }
        AIProvider.LAST_ACTION_SESSIONID = sessionId;

        return client.chatText({
          sessionId,
          threadId,
          workspaceId,
          messageId,
          reasoning,
          reasoningEffort,
          webSearch,
          modelId,
          enableTools,
          // sl-535: Pass language to backend
          language: resolvedLanguage,
        });
      })(),
    ]);
  }
}

/**
 * Stop of a chat answer: closes its stream and ends the answer's server job.
 * Closing the stream alone leaves the answer running.
 */
function stopChatTurn(
  eventSource: EventSource,
  client: CopilotClient,
  sessionId: string,
  workspaceId: string
) {
  eventSource.close();
  client.abortChatStream(sessionId, workspaceId).catch(console.error);
}

/** Turns the events of a chat answer stream into the chunks the chat renders. */
async function* toChatStreamChunks(
  events: AsyncIterable<AffineTextEvent>
): AsyncGenerator<string> {
  for await (const event of events) {
    if (event.type === 'message') {
      yield event.data;
    } else if (event.type === 'reasoning') {
      // Yield as JSON StreamObject so the parsing pipeline handles it
      yield JSON.stringify({
        type: 'reasoning',
        textDelta: event.data,
      });
    } else if (event.type === 'tool-call-start') {
      // sl-718z: Early tool notification — name known, args still streaming
      try {
        const tcs = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'tool-call',
          toolCallId: tcs.toolCallId || '',
          toolName: tcs.toolName,
          displayName: tcs.displayName,
          args: {},
        });
      } catch {
        /* skip malformed */
      }
    } else if (event.type === 'tool-call') {
      // sl-718z: Forward tool-call SSE events as StreamObjects
      try {
        const tc = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'tool-call',
          toolCallId: tc.id,
          toolName: tc.name,
          displayName: tc.displayName,
          args:
            typeof tc.arguments === 'string'
              ? JSON.parse(tc.arguments)
              : tc.arguments,
        });
      } catch {
        /* skip malformed tool-call events */
      }
    } else if (event.type === 'tool-execution-started') {
      // sl-z4tb.5: Forward execution-started for immediate UI feedback
      try {
        const tes = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'tool-execution-started',
          toolCalls: tes.toolCalls,
          totalCount: tes.totalCount,
        });
      } catch {
        /* skip malformed */
      }
    } else if (event.type === 'tool-result') {
      // sl-718z: Forward tool-result SSE events as StreamObjects
      try {
        const tr = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'tool-result',
          toolCallId: tr.toolCallId,
          toolName: tr.name,
          displayName: tr.displayName,
          args: tr.args ?? {},
          result: tr.result,
          affectedRefs: tr.affectedRefs ?? tr.result?.affectedRefs,
          ...(tr.readOnly === true ? { readOnly: true } : {}),
          durationMs: tr.durationMs,
        });
      } catch {
        /* skip malformed tool-result events */
      }
    } else if (event.type === 'usage') {
      // sl-opil.15.3: Forward usage/credits info for managed AI calls
      try {
        const u = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'usage',
          creditsUsed: u.creditsUsed,
          model: u.model,
          promptTokens: u.promptTokens,
          completionTokens: u.completionTokens,
          totalTokens: u.totalTokens,
        });
      } catch {
        /* skip malformed usage events */
      }
    } else if (event.type === 'routing') {
      // sl-sf9o: Forward routing telemetry for badge display
      try {
        const routing = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'routing',
          ...routing,
        });
      } catch {
        /* skip malformed routing events */
      }
    } else if (event.type === 'meta') {
      // sl-k8wk.2.2: Forward low-balance meta-event
      try {
        const meta = JSON.parse(event.data);
        yield JSON.stringify({
          type: 'meta',
          ...meta,
        });
      } catch {
        /* skip malformed meta events */
      }
    }
  }
}

/**
 * Follows the running answer of a chat session, e.g. after the chat was
 * opened again: yields the chunks written so far and then the new ones, like
 * textToText. Yields nothing when no answer is running. `stop` ends the
 * answer; `close` only stops following it.
 */
export function attachToChatTurn({
  client,
  workspaceId,
  sessionId,
  stop,
  close,
}: {
  client: CopilotClient;
  workspaceId: string;
  sessionId: string;
  stop: AbortSignal;
  close: AbortSignal;
}): AsyncIterable<string> {
  return {
    [Symbol.asyncIterator]: async function* () {
      if (stop.aborted || close.aborted) return;
      const eventSource = client.attachChatStream(sessionId, workspaceId);
      const onStop = () =>
        stopChatTurn(eventSource, client, sessionId, workspaceId);
      stop.addEventListener('abort', onStop, { once: true });
      try {
        yield* toChatStreamChunks(
          toTextStream(eventSource, { signal: AbortSignal.any([stop, close]) })
        );
      } finally {
        stop.removeEventListener('abort', onStop);
      }
    },
  };
}

// Only one image is currently being processed
export function toImage({
  content,
  sessionId,
  threadId,
  attachments,
  context,
  seed,
  mateId,
  signal,
  timeout = TIMEOUT,
  retry = false,
  endpoint,
  client,
  workspaceId,
  messageId: retryMessageId,
  assertContextCurrent,
}: ToImageOptions) {
  if (retry && !retryMessageId) {
    throw new Error(
      'toImage: retry=true requires messageId of the user message being retried'
    );
  }
  let messageId: string | undefined = retry ? retryMessageId : undefined;
  return {
    [Symbol.asyncIterator]: async function* () {
      if (signal?.aborted) throw signal.reason;
      if (!workspaceId) {
        throw new Error('toImage requires workspaceId');
      }
      if (!retry) {
        messageId = await createMessage({
          client,
          workspaceId,
          sessionId,
          threadId,
          content,
          attachments,
          context,
          signal,
          assertContextCurrent,
        });
      }
      const eventSource = client.imagesStream(
        sessionId,
        threadId,
        workspaceId,
        messageId,
        seed,
        mateId,
        endpoint
      );
      AIProvider.LAST_ACTION_SESSIONID = sessionId;

      for await (const event of toTextStream(eventSource, {
        timeout,
        signal,
      })) {
        if (event.type === 'attachment') {
          yield event.data;
        }
      }
    },
  };
}
